import axios from '@/plugins/axios'
import { deviceHint } from '@/utils/beacon-hints'

// iPads on iPadOS 13+ send a Mac desktop UA, so the server-side UA parsing can't see
// them — attach the client-side device hint to every beacon (undefined spreads to nothing)
const withDevice = data => ({ ...data, device: deviceHint() })

export const getCollectionAnalytics = (collectionId, params) =>
  axios.get(`/v1/collections/${collectionId}/analytics`, { params })

export const getCollectionItemAnalytics = (collectionId, itemId, params) =>
  axios.get(`/v1/collections/${collectionId}/analytics/items/${itemId}`, { params })

// Fire-and-forget view beacon (collection view, or item detail view when itemId is set) —
// used by public pages and in-app views alike. Everyone counts (owner and members too);
// the server validates the target, dedups per identity per 30 minutes, and gates
// non-public collections to their owner/members. Deliberately cookieless (no credentials):
// visitor identity is a server-side salted daily fingerprint, never anything stored client-side.
export const trackPublicView = data => axios.post('/v1/public/track', withDevice(data))

// Teaser-impression beacon — same endpoint and dedup; `teaser: true` counts an impression instead of
// a view. Pass { collectionId } for a collection card, { collectionId, itemId } for an item card.
// Impressions burst on scroll (one per card entering the viewport) while the server allows
// 30 beacons per 10s per user across ALL actions — so teasers drain from a FIFO at one per
// 500ms (20/10s), leaving the direct view/clickout senders headroom. The resolved return
// value keeps the sender interface: every caller chains .catch().
const teaserQueue = []
let teaserTimer = null

const drainTeaserQueue = () => {
  if (!teaserQueue.length) {
    teaserTimer = null
    return
  }
  axios.post('/v1/public/track', withDevice({ ...teaserQueue.shift(), teaser: true })).catch(() => {})
  teaserTimer = setTimeout(drainTeaserQueue, 500)
}

export const trackPublicTeaser = data => {
  teaserQueue.push(data)
  if (!teaserTimer) drainTeaserQueue()
  return Promise.resolve()
}

// Clickout beacon — in-app bookmark links navigate directly (no counting redirect), so the
// click is reported out-of-band instead. Pass { collectionId, itemId }.
export const trackClickout = data => axios.post('/v1/public/track', withDevice({ ...data, clickout: true }))
